import React, { useState, useRef, useCallback } from "react";
import { RotateCcw } from "lucide-react";
import { useStudio } from "@/studio/StudioContext";
import Knob from "@/studio/controls/Knob";
import Visualizer from "@/studio/Visualizer";
import InfoDot from "@/studio/InfoDot";
import { EQ_FREQS, EQ_LABELS } from "@/audio/engine";

function Section({ title, accent, children, right }) {
  return (
    <div className="rounded-sm border border-[#262626] bg-[#141414] p-3 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="hw-label" style={{ color: accent }}>
          {title}
        </span>
        {right}
      </div>
      <div className="flex gap-3 justify-around">{children}</div>
    </div>
  );
}

// Bipolar vertical EQ slider (-18..+18 dB) with center detent.
function EqBand({ label, value, onChange, testId }) {
  const ref = useRef(null);
  const dragging = useRef(false);
  const height = 120;
  const travel = height - 14;
  const min = -18;
  const max = 18;
  const range = max - min;
  const norm = (value - min) / range;
  const top = (1 - norm) * travel;

  const setFromY = useCallback(
    (clientY) => {
      const r = ref.current.getBoundingClientRect();
      let rel = clientY - r.top - 7;
      rel = Math.max(0, Math.min(travel, rel));
      const n = 1 - rel / travel;
      let v = min + n * range;
      if (Math.abs(v) < 1.2) v = 0; // snap to 0
      onChange(Math.round(v));
    },
    [travel, range, onChange]
  );

  const onDown = useCallback(
    (e) => {
      dragging.current = true;
      setFromY(e.clientY);
      const move = (ev) => dragging.current && setFromY(ev.clientY);
      const up = () => {
        dragging.current = false;
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      e.preventDefault();
    },
    [setFromY]
  );

  const onWheel = useCallback(
    (e) => {
      e.preventDefault();
      let v = Math.max(min, Math.min(max, value - Math.sign(e.deltaY) * -1));
      onChange(v);
    },
    [value, onChange]
  );

  const color = value === 0 ? "#00e5ff" : value > 0 ? "#00ff66" : "#ff007f";

  return (
    <div className="flex flex-col items-center gap-1 select-none" data-testid={testId}>
      <span className="font-mono-data text-[9px]" style={{ color }}>
        {value > 0 ? "+" : ""}
        {value}
      </span>
      <div
        ref={ref}
        onPointerDown={onDown}
        onWheel={onWheel}
        role="slider"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-label={`EQ ${label}`}
        title={`${label} Hz — drag up to boost, down to cut`}
        className="relative cursor-grab active:cursor-grabbing"
        style={{ width: 20, height, touchAction: "none" }}
      >
        {/* track */}
        <div
          className="absolute left-1/2 -translate-x-1/2 rounded-full inset-panel"
          style={{ width: 4, height: "100%", background: "#000" }}
        />
        {/* center line */}
        <div className="absolute left-0 right-0 top-1/2 h-px bg-[#333]" />
        {/* fill from center to thumb */}
        <div
          className="absolute left-1/2 -translate-x-1/2 rounded-full"
          style={{
            width: 4,
            background: color,
            boxShadow: `0 0 5px ${color}`,
            top: value >= 0 ? top + 7 : "50%",
            height: Math.abs(top + 7 - height / 2),
          }}
        />
        {/* thumb */}
        <div
          className="absolute left-1/2 -translate-x-1/2 rounded-sm"
          style={{
            top,
            width: 18,
            height: 14,
            background: "linear-gradient(180deg,#3a3a3a,#1e1e1e)",
            borderBottom: "2px solid #000",
            boxShadow: "0 2px 4px rgba(0,0,0,0.7)",
          }}
        >
          <div className="absolute top-1/2 left-1 right-1 -translate-y-1/2 h-[2px]" style={{ background: color }} />
        </div>
      </div>
      <span className="hw-label text-[8px] leading-none">{label}</span>
    </div>
  );
}

export default function EffectsRack() {
  const { engine, selectedChannel, setSelectedChannel } = useStudio();
  const [, force] = useState(0);
  const u = () => force((n) => n + 1);
  const ch = engine.started ? engine.getChannel(selectedChannel) : null;

  // Ordered list of routable channels for the selector.
  const channelOptions = engine.started
    ? [
        ["deckA", "Deck A"],
        ["deckB", "Deck B"],
        ["drums", "Drums"],
        ["synth", "Synth"],
        ["player", "Player"],
        ...Object.keys(engine.channels)
          .filter((id) => /^track\d+$/.test(id))
          .sort((a, b) => Number(a.slice(5)) - Number(b.slice(5)))
          .map((id) => [id, engine.channels[id].name]),
        ["master", "Master"],
      ].filter(([id]) => id === "master" || engine.channels[id])
    : [];

  if (!ch) {
    return (
      <div className="flex flex-col h-full" data-testid="effects-rack">
        <div className="px-3 py-2 border-b border-[#262626]">
          <h3 className="font-display text-sm uppercase tracking-widest text-neutral-300">Effects Rack</h3>
        </div>
        <div className="flex-1 flex items-center justify-center text-neutral-600 text-xs">
          Play or interact to power up the audio engine
        </div>
      </div>
    );
  }

  const resetEq = () => {
    EQ_FREQS.forEach((_, i) => ch.setEqBand(i, 0));
    u();
  };

  return (
    <div className="flex flex-col h-full" data-testid="effects-rack">
      <div className="px-3 py-2 border-b border-[#262626] flex items-center justify-between gap-2">
        <h3 className="font-display text-sm uppercase tracking-widest text-neutral-300 flex items-center gap-1.5 shrink-0">
          Effects Rack
          <InfoDot section="effects" label="How the Effects Rack & EQ work" />
        </h3>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="hw-label text-[8px] text-neutral-500 shrink-0">Editing</span>
          <select
            data-testid="fx-channel-select"
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            title="Choose which channel the EQ & effects apply to"
            aria-label="Effects rack target channel"
            className="bg-[#141414] border rounded-sm px-1.5 py-1 text-[10px] font-display uppercase tracking-widest font-bold outline-none cursor-pointer max-w-[130px] truncate"
            style={{ color: ch.color, borderColor: `${ch.color}66` }}
          >
            {channelOptions.map(([id, label]) => (
              <option key={id} value={id} style={{ color: "#e5e5e5", background: "#141414" }}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-3 flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <span className="hw-label" style={{ color: "#a855f7" }}>
            Live Spectrum
          </span>
          <Visualizer height={84} bars={48} />
        </div>

        <Section
          title="8-Band Graphic EQ"
          accent="#00e5ff"
          right={
            <button
              data-testid="eq-reset"
              onClick={resetEq}
              className="flex items-center gap-1 hw-label text-[8px] hover:text-white transition-colors"
            >
              <RotateCcw size={10} /> Flat
            </button>
          }
        >
          {EQ_FREQS.map((f, i) => (
            <EqBand
              key={f}
              label={EQ_LABELS[i]}
              value={ch.state.eq[i]}
              onChange={(v) => {
                ch.setEqBand(i, v);
                u();
              }}
              testId={`eq-band-${i}`}
            />
          ))}
        </Section>

        <Section title="Dynamics & Tone" accent="#ffb800">
          <Knob value={ch.state.fx.comp} min={0} max={1} size={40} color="#ffb800" label="Comp"
            hint="Compressor — evens out loud and quiet parts for a punchier sound"
            onChange={(v) => { ch.setComp(v); u(); }} testId="fx-comp" />
          <Knob value={ch.state.fx.distortion} min={0} max={1} size={40} color="#ff3333" label="Drive"
            hint="Drive / distortion — adds grit and warmth"
            onChange={(v) => { ch.setDistortion(v); u(); }} testId="fx-distortion" />
          <Knob value={ch.state.fx.filterFreq} min={200} max={20000} size={40} color="#a855f7" label="Filter"
            hint="Low-pass filter — turn down to muffle/darken the sound"
            format={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${Math.round(v)}`)}
            onChange={(v) => { ch.setFilter(v); u(); }} testId="fx-filter" />
        </Section>

        <Section title="Space" accent="#00ff66">
          <Knob value={ch.state.fx.delay} min={0} max={1} size={40} color="#00ff66" label="Delay"
            hint="Delay — repeating echoes of the sound"
            onChange={(v) => { ch.setDelay(v); u(); }} testId="fx-delay" />
          <Knob value={ch.state.fx.reverb} min={0} max={1} size={40} color="#00e5ff" label="Reverb"
            hint="Reverb — adds room/hall ambience and space"
            onChange={(v) => { ch.setReverb(v); u(); }} testId="fx-reverb" />
        </Section>
      </div>
    </div>
  );
}
